import React, { useState } from 'react';
import { reportsAPI } from '../../utils/api';
import { formatDate, formatCurrency, todayInputDate } from '../../utils/helpers';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const REPORT_TYPES = [
  { key: 'profit', label: 'Profit & Loss', icon: '💹' },
  { key: 'members', label: 'Members', icon: '👥' },
  { key: 'payments', label: 'Payments', icon: '💳' },
  { key: 'attendance', label: 'Attendance', icon: '✅' },
  { key: 'supplements', label: 'Supplements', icon: '💊' },
  { key: 'expenses', label: 'Expenses', icon: '💸' },
];

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#f97316'];

function Reports() {
  const [activeReport, setActiveReport] = useState('profit');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: todayInputDate()
  });

  const fetchReport = async () => {
    setLoading(true);
    setData(null);
    try {
      const params = { startDate: dateRange.start, endDate: dateRange.end };
      let res;
      switch (activeReport) {
        case 'profit': res = await reportsAPI.getProfitLoss(params); break;
        case 'members': res = await reportsAPI.getMembers(params); break;
        case 'payments': res = await reportsAPI.getPayments(params); break;
        case 'attendance': res = await reportsAPI.getAttendance(params); break;
        case 'supplements': res = await reportsAPI.getSupplements(params); break;
        case 'expenses': res = await reportsAPI.getExpenses(params); break;
        default: break;
      }
      setData(res.data);
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    const title = REPORT_TYPES.find(r => r.key === activeReport)?.label || 'Report';
    doc.setFontSize(18);
    doc.text(`GymPro - ${title} Report`, 20, 20);
    doc.setFontSize(12);
    doc.text(`Period: ${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`, 20, 30);
    doc.text(`Generated: ${formatDate(new Date())}`, 20, 38);

    let y = 50;
    doc.setFontSize(11);

    if (activeReport === 'profit' && data) {
      const rows = [
        ['Membership Income', formatCurrency(data.membershipIncome)],
        ['Supplement Income', formatCurrency(data.supplementIncome)],
        ['Total Income', formatCurrency(data.totalIncome)],
        ['Total Expenses', formatCurrency(data.totalExpenses)],
        ['Net Profit', formatCurrency(data.netProfit)],
      ];
      rows.forEach(([label, value]) => {
        doc.text(label, 20, y);
        doc.text(value, 120, y);
        y += 10;
      });
    } else if (activeReport === 'members' && data?.members) {
      data.members.slice(0, 30).forEach(m => {
        doc.text(`${m.name} | ${m.memberId} | ${m.plan} | ${m.status}`, 20, y);
        y += 8;
        if (y > 270) { doc.addPage(); y = 20; }
      });
    } else if (activeReport === 'payments' && data?.payments) {
      data.payments.slice(0, 30).forEach(p => {
        doc.text(`${p.memberId?.name || 'N/A'} | ${formatCurrency(p.amountPaid)} | ${p.paymentMode} | ${formatDate(p.paidDate)}`, 20, y);
        y += 8;
        if (y > 270) { doc.addPage(); y = 20; }
      });
    } else if (activeReport === 'expenses' && data?.expenses) {
      data.expenses.slice(0, 30).forEach(e => {
        doc.text(`${e.category} | ${formatCurrency(e.amount)} | ${formatDate(e.date)}`, 20, y);
        y += 8;
        if (y > 270) { doc.addPage(); y = 20; }
      });
    }

    doc.save(`GymPro_${title}_${dateRange.start}_${dateRange.end}.pdf`);
    toast.success('PDF exported!');
  };

  const exportExcel = () => {
    if (!data) return;
    const title = REPORT_TYPES.find(r => r.key === activeReport)?.label || 'Report';
    let rows = [];

    if (activeReport === 'profit') {
      rows = [
        ['Item', 'Amount'],
        ['Membership Income', data.membershipIncome],
        ['Supplement Income', data.supplementIncome],
        ['Total Income', data.totalIncome],
        ['Total Expenses', data.totalExpenses],
        ['Net Profit', data.netProfit],
      ];
    } else if (activeReport === 'members') {
      rows = [['Name', 'Member ID', 'Plan', 'Shift', 'Status', 'Joining Date', 'Expiry Date', 'Phone'],
        ...(data.members || []).map(m => [m.name, m.memberId, m.plan, m.shift, m.status, formatDate(m.joiningDate), formatDate(m.expiryDate), m.phone])];
    } else if (activeReport === 'payments') {
      rows = [['Member', 'Amount', 'Paid', 'Balance', 'Mode', 'Status', 'Date'],
        ...(data.payments || []).map(p => [p.memberId?.name, p.amount, p.amountPaid, p.balanceRemaining, p.paymentMode, p.status, formatDate(p.paidDate)])];
    } else if (activeReport === 'attendance') {
      rows = [['Member', 'Date', 'Shift', 'Checked In'],
        ...(data.records || []).map(a => [a.memberId?.name, a.date, a.shift, a.checkedIn ? 'Yes' : 'No'])];
    } else if (activeReport === 'supplements') {
      rows = [['Product', 'Brand', 'Buyer', 'Qty', 'Amount', 'Date'],
        ...(data.sales || []).map(s => [s.supplementId?.name, s.supplementId?.brand, s.memberId?.name || s.walkinName, s.quantity, s.totalAmount, formatDate(s.saleDate)])];
    } else if (activeReport === 'expenses') {
      rows = [['Category', 'Amount', 'Date', 'Description', 'Recurring'],
        ...(data.expenses || []).map(e => [e.category, e.amount, formatDate(e.date), e.description, e.isRecurring ? 'Yes' : 'No'])];
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title);
    XLSX.writeFile(wb, `GymPro_${title}_${dateRange.start}_${dateRange.end}.xlsx`);
    toast.success('Excel exported!');
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Reports</h2>
        {data && (
          <div className="flex gap-2">
            <button onClick={exportExcel} className="btn-secondary text-sm">📊 Export Excel</button>
            <button onClick={exportPDF} className="btn-secondary text-sm">📄 Export PDF</button>
          </div>
        )}
      </div>

      {/* Report Type Selector */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {REPORT_TYPES.map(r => (
          <button
            key={r.key}
            onClick={() => { setActiveReport(r.key); setData(null); }}
            className={`card py-3 text-center transition-all hover:shadow-md ${activeReport === r.key ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
          >
            <div className="text-2xl">{r.icon}</div>
            <p className="text-xs font-medium text-gray-700 mt-1">{r.label}</p>
          </button>
        ))}
      </div>

      {/* Date Range + Generate */}
      <div className="card py-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">From:</label>
            <input type="date" className="input w-auto text-sm" value={dateRange.start} onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))} />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">To:</label>
            <input type="date" className="input w-auto text-sm" value={dateRange.end} onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))} />
          </div>
          <button onClick={fetchReport} className="btn-primary text-sm">
            Generate Report
          </button>
        </div>
      </div>

      {/* Report Content */}
      {loading && <LoadingSpinner message="Generating report..." />}

      {!loading && data && (
        <>
          {/* Profit & Loss Report */}
          {activeReport === 'profit' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Membership Income', value: data.membershipIncome, color: 'text-blue-600' },
                  { label: 'Supplement Income', value: data.supplementIncome, color: 'text-purple-600' },
                  { label: 'Total Income', value: data.totalIncome, color: 'text-green-600' },
                  { label: 'Total Expenses', value: data.totalExpenses, color: 'text-red-600' },
                  { label: 'Net Profit', value: data.netProfit, color: data.netProfit >= 0 ? 'text-green-700' : 'text-red-700' },
                ].map(item => (
                  <div key={item.label} className="card text-center">
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <p className={`text-xl font-bold mt-1 ${item.color}`}>{formatCurrency(item.value)}</p>
                  </div>
                ))}
              </div>
              <div className="card">
                <h3 className="font-semibold text-gray-700 mb-4">Revenue Breakdown</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={[{ name: 'Membership', value: data.membershipIncome }, { name: 'Supplements', value: data.supplementIncome }]}
                      cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      <Cell fill="#3b82f6" /><Cell fill="#10b981" />
                    </Pie>
                    <Tooltip formatter={v => formatCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Members Report */}
          {activeReport === 'members' && data.members && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {[
                  { label: 'Total', value: data.stats?.total, color: 'text-blue-600' },
                  { label: 'Active', value: data.stats?.active, color: 'text-green-600' },
                  { label: 'Expiring Soon', value: data.stats?.expiringSoon, color: 'text-yellow-600' },
                  { label: 'Expired', value: data.stats?.expired, color: 'text-red-600' },
                  { label: 'Frozen', value: data.stats?.frozen, color: 'text-blue-600' },
                ].map(item => (
                  <div key={item.label} className="card text-center">
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${item.color}`}>{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="card p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b border-gray-100">
                      <th className="table-header">Name</th>
                      <th className="table-header">ID</th>
                      <th className="table-header">Plan</th>
                      <th className="table-header">Shift</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Joined</th>
                      <th className="table-header">Expires</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {data.members.map(m => (
                        <tr key={m._id} className="hover:bg-gray-50">
                          <td className="table-cell font-medium">{m.name}</td>
                          <td className="table-cell"><span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded">{m.memberId}</span></td>
                          <td className="table-cell text-sm">{m.plan}</td>
                          <td className="table-cell text-sm">{m.shift}</td>
                          <td className="table-cell"><StatusBadge status={m.status} /></td>
                          <td className="table-cell text-xs">{formatDate(m.joiningDate)}</td>
                          <td className="table-cell text-xs">{formatDate(m.expiryDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Payments Report */}
          {activeReport === 'payments' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="card text-center">
                  <p className="text-xs text-gray-500">Total Collected</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(data.totalCollected)}</p>
                </div>
                <div className="card text-center">
                  <p className="text-xs text-gray-500">Pending Dues</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{data.pending?.length || 0} members</p>
                </div>
              </div>
              <div className="card p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b border-gray-100">
                      <th className="table-header">Member</th>
                      <th className="table-header">Amount</th>
                      <th className="table-header">Paid</th>
                      <th className="table-header">Mode</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Date</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {data.payments?.map(p => (
                        <tr key={p._id} className="hover:bg-gray-50">
                          <td className="table-cell font-medium">{p.memberId?.name || 'N/A'}</td>
                          <td className="table-cell">{formatCurrency(p.amount)}</td>
                          <td className="table-cell text-green-600 font-medium">{formatCurrency(p.amountPaid)}</td>
                          <td className="table-cell"><span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{p.paymentMode}</span></td>
                          <td className="table-cell"><StatusBadge status={p.status} /></td>
                          <td className="table-cell text-xs">{formatDate(p.paidDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Attendance Report */}
          {activeReport === 'attendance' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="card text-center">
                  <p className="text-xs text-gray-500">Morning Check-ins</p>
                  <p className="text-2xl font-bold text-yellow-600 mt-1">{data.morningCount}</p>
                </div>
                <div className="card text-center">
                  <p className="text-xs text-gray-500">Evening Check-ins</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">{data.eveningCount}</p>
                </div>
                <div className="card text-center">
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-2xl font-bold text-gray-700 mt-1">{data.total}</p>
                </div>
              </div>
              <div className="card p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b border-gray-100">
                      <th className="table-header">Member</th>
                      <th className="table-header">Date</th>
                      <th className="table-header">Shift</th>
                      <th className="table-header">Status</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {data.records?.filter(r => r.checkedIn).slice(0, 100).map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="table-cell font-medium">{r.memberId?.name || 'N/A'}</td>
                          <td className="table-cell text-xs">{r.date}</td>
                          <td className="table-cell"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${r.shift === 'Morning' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{r.shift}</span></td>
                          <td className="table-cell"><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Present</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Supplements Report */}
          {activeReport === 'supplements' && (
            <div className="space-y-4">
              <div className="card text-center">
                <p className="text-xs text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(data.totalRevenue)}</p>
              </div>
              {data.byProduct && Object.keys(data.byProduct).length > 0 && (
                <div className="card">
                  <h3 className="font-semibold text-gray-700 mb-4">Revenue by Product</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={Object.entries(data.byProduct).map(([name, value]) => ({ name, value }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${v}`} />
                      <Tooltip formatter={v => formatCurrency(v)} />
                      <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="card p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b border-gray-100">
                      <th className="table-header">Product</th>
                      <th className="table-header">Buyer</th>
                      <th className="table-header">Qty</th>
                      <th className="table-header">Amount</th>
                      <th className="table-header">Date</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {data.sales?.map(s => (
                        <tr key={s._id} className="hover:bg-gray-50">
                          <td className="table-cell font-medium">{s.supplementId?.name}</td>
                          <td className="table-cell">{s.memberId?.name || s.walkinName || 'Walk-in'}</td>
                          <td className="table-cell">{s.quantity}</td>
                          <td className="table-cell font-medium text-green-600">{formatCurrency(s.totalAmount)}</td>
                          <td className="table-cell text-xs">{formatDate(s.saleDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Expenses Report */}
          {activeReport === 'expenses' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="card text-center">
                  <p className="text-xs text-gray-500">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(data.total)}</p>
                </div>
                {data.byCategory && (
                  <div className="card">
                    <h3 className="text-xs font-semibold text-gray-500 mb-2">By Category</h3>
                    {Object.entries(data.byCategory).map(([cat, amt]) => (
                      <div key={cat} className="flex justify-between text-sm py-1">
                        <span className="text-gray-600">{cat}</span>
                        <span className="font-medium text-red-600">{formatCurrency(amt)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="card p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b border-gray-100">
                      <th className="table-header">Category</th>
                      <th className="table-header">Amount</th>
                      <th className="table-header">Date</th>
                      <th className="table-header">Description</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {data.expenses?.map(e => (
                        <tr key={e._id} className="hover:bg-gray-50">
                          <td className="table-cell"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{e.category}</span></td>
                          <td className="table-cell font-bold text-red-600">{formatCurrency(e.amount)}</td>
                          <td className="table-cell text-xs">{formatDate(e.date)}</td>
                          <td className="table-cell text-sm text-gray-500">{e.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {!loading && !data && (
        <div className="card text-center py-16">
          <div className="text-5xl mb-4">📊</div>
          <p className="text-gray-500 font-medium">Select a report type and click Generate</p>
          <p className="text-gray-400 text-sm mt-1">Set a date range and generate your report</p>
        </div>
      )}
    </div>
  );
}

export default Reports;
